import React, { useEffect, useState } from "react";
import { RefreshCw, ExternalLink, TrendingUp, TrendingDown, Minus, Loader2, WifiOff } from "lucide-react";
import { Button } from "./ui/button";
import TradingViewChart from "./TradingViewChart";
import TVWidget from "./TVWidget";
import api from "../lib/api";
import { toast } from "sonner";

// ─── Shiller CAPE helpers ────────────────────────────────────────────────────
function capeInfo(v) {
  if (v == null) return { color: "text-zinc-500", label: "—", icon: Minus };
  if (v < 20)   return { color: "text-emerald-400", label: "Günstig",              icon: TrendingDown };
  if (v < 25)   return { color: "text-emerald-300", label: "Fair bewertet",         icon: Minus };
  if (v < 30)   return { color: "text-amber-400",   label: "Teuer",                icon: TrendingUp };
  if (v < 35)   return { color: "text-orange-400",  label: "Sehr teuer",           icon: TrendingUp };
  return               { color: "text-rose-400",    label: "Extrem überbewertet",  icon: TrendingUp };
}

// ─── Buffett Indicator helpers ───────────────────────────────────────────────
function buffettInfo(v) {
  if (v == null)  return { color: "text-zinc-500", label: "—",                    icon: Minus };
  if (v < 80)     return { color: "text-emerald-400", label: "Stark unterbewertet", icon: TrendingDown };
  if (v < 115)    return { color: "text-emerald-300", label: "Unterbewertet",       icon: TrendingDown };
  if (v < 140)    return { color: "text-amber-300",   label: "Fair bewertet",       icon: Minus };
  if (v < 167)    return { color: "text-amber-400",   label: "Überbewertet",        icon: TrendingUp };
  if (v < 200)    return { color: "text-orange-400",  label: "Stark überbewertet",  icon: TrendingUp };
  return                 { color: "text-rose-400",    label: "Spielt mit dem Feuer", icon: TrendingUp };
}

// ─── Skeleton ────────────────────────────────────────────────────────────────
function Shimmer({ className = "" }) {
  return <div className={`bg-zinc-800 animate-pulse rounded-sm ${className}`} />;
}

function MacroSkeleton() {
  return (
    <div className="p-4 md:p-6 space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Shimmer className="h-7 w-48" />
          <Shimmer className="h-3 w-64" />
        </div>
        <Shimmer className="h-8 w-36" />
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[0,1,2].map(i => (
          <div key={i} className="border border-zinc-800 bg-zinc-950 p-4 space-y-3">
            <Shimmer className="h-3 w-32" />
            <Shimmer className="h-10 w-24" />
            <Shimmer className="h-3 w-20" />
            <Shimmer className="h-1.5 w-full" />
            <Shimmer className="h-2 w-full" />
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[0,1,2].map(i => (
          <div key={i} className="border border-zinc-800 bg-zinc-950 p-3 space-y-2">
            <Shimmer className="h-3 w-3/4" />
            {[0,1,2,3,4].map(j => <Shimmer key={j} className="h-2 w-full" />)}
          </div>
        ))}
      </div>

      {/* Chart placeholder */}
      <div className="space-y-2">
        <Shimmer className="h-3 w-56" />
        <div className="border border-zinc-800 bg-zinc-950 flex items-center justify-center" style={{ height: 400 }}>
          <div className="flex flex-col items-center gap-3 text-zinc-600">
            <Loader2 size={24} className="animate-spin" />
            <span className="text-xs font-display uppercase tracking-widest">Lade Chart…</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ────────────────────────────────────────────────────────────────
function KPICard({ title, value, unit = "", info, note, barPct }) {
  const { color, label, icon: Icon } = info;
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-4 space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display">{title}</div>
      <div className="flex items-end gap-2">
        <span className={`font-mono-num font-black text-4xl leading-none ${color}`}>
          {value != null ? `${value}${unit}` : "—"}
        </span>
        {Icon && value != null && <Icon size={18} className={`${color} mb-1`} />}
      </div>
      <div className={`text-xs font-display font-bold uppercase tracking-wider ${color}`}>{label}</div>
      {barPct != null && (
        <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${color.replace("text-", "bg-")}`}
            style={{ width: `${Math.min(100, Math.max(2, barPct))}%` }}
          />
        </div>
      )}
      {note && <div className="text-[10px] text-zinc-600">{note}</div>}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
export default function Makro() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try { setData(await api.getMacro()); }
    catch { setLoadError(true); }
    finally { setLoading(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try { setData(await api.refreshMacro()); toast.success("Makro-Daten aktualisiert."); }
    catch { toast.error("Refresh fehlgeschlagen."); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const cape    = data?.cape;
  const buffett = data?.buffett?.ratio;
  const fedRate = data?.fed_rate;
  const updatedAt = data?.updated_at
    ? new Date(data.updated_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })
    : null;

  const fedInfo = fedRate == null
    ? { color: "text-zinc-500", label: "—", icon: Minus }
    : fedRate > 4.5
      ? { color: "text-rose-400",    label: "Stark restriktiv",  icon: TrendingUp }
      : fedRate > 2.5
        ? { color: "text-amber-400",   label: "Restriktiv",         icon: TrendingUp }
        : fedRate > 1
          ? { color: "text-amber-300",   label: "Neutral",             icon: Minus }
          : { color: "text-emerald-400", label: "Expansiv",            icon: TrendingDown };

  if (loading) return <MacroSkeleton />;

  if (loadError) return (
    <div className="p-4 md:p-6 flex flex-col items-center justify-center gap-5 min-h-[60vh] text-center">
      <div className="w-14 h-14 border border-rose-800 flex items-center justify-center">
        <WifiOff size={24} className="text-rose-500" />
      </div>
      <div>
        <div className="font-display font-black text-lg tracking-tight mb-1">Makro-Daten nicht verfügbar</div>
        <p className="text-muted-foreground text-sm max-w-sm leading-relaxed">
          Die Verbindung zum Backend ist fehlgeschlagen oder die externen Quellen
          (FRED, multpl.com) sind nicht erreichbar.
        </p>
      </div>
      <Button onClick={load} variant="outline" className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs h-9 px-6">
        <RefreshCw size={13} className="mr-2" /> Erneut versuchen
      </Button>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-full overflow-x-hidden" data-testid="makro-page">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display font-black text-2xl tracking-tight">Makro-Indikatoren</h1>
          <div className="text-[10px] text-zinc-500 mt-0.5 font-mono-num">
            {updatedAt ? `Zuletzt: ${updatedAt} · Auto-Refresh 24h` : "Globale Bewertungsindikatoren"}
          </div>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs h-8"
        >
          <RefreshCw size={12} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Jetzt aktualisieren
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPICard
          title="Shiller CAPE (KGV)"
          value={cape != null ? cape.toFixed(1) : null}
          info={capeInfo(cape)}
          barPct={cape != null ? (cape / 50) * 100 : null}
          note={`Hist. Durchschnitt 17,4 · Median 16,1 · Quelle: multpl.com`}
        />
        <KPICard
          title="Buffett Indikator"
          value={buffett != null ? buffett.toFixed(1) : null}
          unit="%"
          info={buffettInfo(buffett)}
          barPct={buffett != null ? (buffett / 250) * 100 : null}
          note={data?.buffett
            ? `Wilshire 5000: $${data.buffett.market_cap_bn?.toLocaleString("de-DE")} Mrd · BIP: $${data.buffett.gdp_bn?.toLocaleString("de-DE")} Mrd · Quelle: FRED`
            : "US Marktkapital. / BIP · Quelle: FRED"
          }
        />
        <KPICard
          title="Fed Funds Rate"
          value={fedRate != null ? fedRate.toFixed(2) : null}
          unit="%"
          info={fedInfo}
          barPct={fedRate != null ? (fedRate / 8) * 100 : null}
          note="Effektiver Tagesgeldsatz · Quelle: FRED (FEDFUNDS)"
        />
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[10px]">
        <div className="border border-zinc-800 bg-zinc-950 p-3 space-y-1.5">
          <div className="font-display font-bold uppercase tracking-wider text-zinc-400">Shiller CAPE — Bewertung S&amp;P 500</div>
          {[["< 20", "Günstig", "text-emerald-400"], ["20–25", "Fair bewertet", "text-emerald-300"], ["25–30", "Teuer", "text-amber-400"], ["30–35", "Sehr teuer", "text-orange-400"], ["> 35", "Extrem überbewertet", "text-rose-400"]].map(([r, l, c]) => (
            <div key={r} className="flex justify-between">
              <span className="text-zinc-500 font-mono-num">{r}</span>
              <span className={`font-bold ${c}`}>{l}</span>
            </div>
          ))}
        </div>
        <div className="border border-zinc-800 bg-zinc-950 p-3 space-y-1.5">
          <div className="font-display font-bold uppercase tracking-wider text-zinc-400">Buffett Indikator</div>
          {[["< 80%", "Stark unterbewertet", "text-emerald-400"], ["80–115%", "Unterbewertet", "text-emerald-300"], ["115–140%", "Fair bewertet", "text-amber-300"], ["140–167%", "Überbewertet", "text-amber-400"], ["167–200%", "Stark überbewertet", "text-orange-400"], ["> 200%", "Spielt mit dem Feuer", "text-rose-400"]].map(([r, l, c]) => (
            <div key={r} className="flex justify-between">
              <span className="text-zinc-500 font-mono-num">{r}</span>
              <span className={`font-bold ${c}`}>{l}</span>
            </div>
          ))}
        </div>
        <div className="border border-zinc-800 bg-zinc-950 p-3 space-y-1.5 col-span-2 md:col-span-1">
          <div className="font-display font-bold uppercase tracking-wider text-zinc-400">Historischer Kontext</div>
          <div className="text-zinc-500 leading-relaxed">
            <span className="font-bold text-zinc-300">1929 Crash:</span> CAPE ~30 · Buffett n/a<br/>
            <span className="font-bold text-zinc-300">Dotcom 2000:</span> CAPE ~44 · Buffett ~183%<br/>
            <span className="font-bold text-zinc-300">Finanzkrise 2009:</span> CAPE ~13 · Buffett ~55%<br/>
            <span className="font-bold text-zinc-300">Mai 2026:</span> CAPE ~41 · Buffett ~231%
          </div>
        </div>
      </div>

      {/* FedWatch */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display">
              CME FedWatch — Fed Funds Futures (CBOT:ZQ1!)
            </div>
            <div className="text-[10px] text-zinc-600 mt-0.5">
              Preis = 100 − implizierter Zinssatz. Zeigt Markterwartung für künftige Fed-Sitzungen.
            </div>
          </div>
          <a href="https://www.cmegroup.com/markets/interest-rates/cme-fedwatch-tool.html" target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs h-7">
              <ExternalLink size={11} className="mr-1" /> Vollständige FedWatch Tabelle (CME)
            </Button>
          </a>
        </div>
        <TradingViewChart symbol="CBOT:ZQ1!" height={400} />
        <div className="mt-1 text-[10px] text-zinc-600">
          Die vollständige Wahrscheinlichkeitstabelle (bedingte Meeting-Wahrscheinlichkeiten wie im Screenshot) ist direkt beim CME Group FedWatch Tool verfügbar → Link oben.
        </div>
      </div>

      {/* Economic Calendar */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display mb-2">
          US Wirtschaftskalender · Fed-relevante Events
        </div>
        <TVWidget
          widgetType="events"
          height={280}
          config={{ width: "100%", height: 280, importanceFilter: "1", countryFilter: "us" }}
          testid="tvw-makro-calendar"
        />
      </div>

    </div>
  );
}
